/**
 * Página pública /vinculacion/:token — vinculación de un municipio nuevo
 * (Sprint 25-4, mockup M3).
 *
 * La abre un funcionario (alcalde o secretario de planeación) desde un
 * correo, después de que Kaptar y el municipio ya negociaron la
 * membresía y firmaron el contrato — por eso los pasos 1-2 del stepper
 * ("Propuesta aceptada", "Contrato firmado") llegan siempre en `done`:
 * si el municipio tiene un token de vinculación, es porque ya pasó por
 * ahí. Lo único que decide esta pantalla es el paso 3 (Pago).
 *
 * Ruta PÚBLICA — sin sesión, sin `ProtectedRoute`, sin `AppShell` (mismo
 * patrón que `AceptarInvitacion.tsx`). `fetchVinculacion`/
 * `pagarVinculacion` usan `requestPublic()` (ver `lib/api/membresia.ts`)
 * así que nunca mandan `Authorization`, ni siquiera si el navegador
 * tiene una sesión de Supabase activa en otra pestaña.
 *
 * Flujo:
 * 1. GET `/membresia/vinculacion/{token}` al montar decide la pantalla:
 *    404 (enlace no válido), 410 (enlace vencido o reemplazado —
 *    típico si Kaptar generó uno nuevo), o el contenido normal según
 *    `estado_funnel`/`paid`.
 * 2. Si `esperando_pago`: stepper en paso 3, card de plan + checklist de
 *    "qué pasa al pagar", CTA "Pagar la membresía" -> POST `/pagar`
 *    (pago SIMULADO, igual que `RenovarMembresia`). 503 = el flag de
 *    pago simulado está apagado en este entorno: se muestra el
 *    `detail` del backend en un banner y la página se queda igual (no
 *    hay nada distinto que reintentar).
 * 3. Al pagar con éxito, la respuesta trae la lista de invitaciones
 *    (`invitaciones: [{email_masked, status}]`) y se re-renderiza en el
 *    mismo estado "activa" sin volver a pedir nada al backend.
 * 4. Si alguien reabre el link DESPUÉS de que ya se pagó (otra sesión,
 *    otro día), el GET ya viene con `paid: true` / `estado_funnel:
 *    'activa'` — pero ese DTO no trae la lista de invitaciones (solo
 *    `invitations_count`), así que esa variante muestra el conteo, no
 *    la lista con emails. Lo mismo si el POST `/pagar` es idempotente
 *    y vuelve con `invitaciones: []` (las invitaciones ya habían salido
 *    en un pago anterior): una lista vacía no es información, el
 *    conteo del GET sí (fix final F10). En ningún caso se desenmascara
 *    un email: se muestra `email_masked` tal como lo manda el backend.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  ApiError,
  fetchVinculacion,
  pagarVinculacion,
  type InvitacionResultadoDTO,
  type VinculacionPagoDTO,
} from "@/lib/api";
import { track } from "@/lib/analytics";
import {
  formatoCOP,
  MEMBRESIA_PRECIOS,
  type MembresiaPeriod,
  type MembresiaTier,
} from "@/lib/membresia-precios";

const SOPORTE_MAILTO = "mailto:soporte@kaptar.com.co";
// Fix diferido de la Task 11 (b): antes esta pantalla reusaba
// `ERROR_GENERICO_PAGO` ("No pudimos procesar el pago...") tambien como
// fallback del GET inicial -- un texto de pago en una pantalla que
// todavia no llego a pagar nada. Ahora cada fallo tiene su propio
// generico.
const ERROR_GENERICO_CARGA = "No pudimos cargar tu enlace de vinculación. Inténtalo de nuevo.";
const ERROR_GENERICO_PAGO = "No pudimos procesar el pago. Inténtalo de nuevo.";

const TIER_LABELS: Record<string, string> = {
  esencial: "Esencial",
  pro: "Pro",
};

/** En minúscula a propósito — así se lee en el mockup: "Esencial · anual". */
const PERIOD_LABELS: Record<string, string> = {
  trimestral: "trimestral",
  semestral: "semestral",
  annual: "anual",
};

const INVITACION_STATUS_LABELS: Record<InvitacionResultadoDTO["status"], string> = {
  enviada: "Invitación enviada",
  ya_era_miembro: "Ya era miembro",
  promovido_a_admin: "Promovido a admin",
};

function esTierValido(v: string): v is MembresiaTier {
  return v === "esencial" || v === "pro";
}

function esPeriodoValido(v: string): v is MembresiaPeriod {
  return v === "trimestral" || v === "semestral" || v === "annual";
}

function tierLabel(tier: string): string {
  return TIER_LABELS[tier] ?? tier;
}

function periodLabel(period: string): string {
  return PERIOD_LABELS[period] ?? period;
}

/** `null` cuando tier/period no son valores reconocidos — nunca se inventa un precio. */
function precioDe(tier: string, period: string): number | null {
  if (!esTierValido(tier) || !esPeriodoValido(period)) return null;
  return MEMBRESIA_PRECIOS[tier][period];
}

export function Vinculacion() {
  const { token } = useParams<{ token: string }>();
  // Datos de la respuesta de pago exitosa — reemplaza al GET original
  // para pintar el estado "activa" con la lista de invitaciones (el GET
  // no la trae, ver punto 4 del docstring).
  const [pago, setPago] = useState<VinculacionPagoDTO | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["vinculacion", token],
    queryFn: () => fetchVinculacion(token as string),
    enabled: Boolean(token),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => pagarVinculacion(token as string),
    onSuccess: (respuesta) => {
      setPago(respuesta);
      track({ event_name: "vinculacion_pagada" });
    },
  });

  // Telemetría -- 1 fire cuando el GET trae datos (nunca en el
  // skeleton ni en error, ver guard `!data`). Dep en `data`, no en
  // `pago`: pagar solo actualiza el estado local `pago`, no vuelve a
  // pedir el GET, así que `data` no cambia de referencia y este efecto
  // no se re-dispara cuando la pantalla pasa a "activa" por el pago
  // (ese momento ya tiene su propio evento, `vinculacion_pagada`).
  useEffect(() => {
    if (!data) return;
    track({
      event_name: "vinculacion_viewed",
      properties: { estado: data.estado_funnel },
    });
  }, [data]);

  if (isLoading) {
    return (
      <Shell>
        <div data-testid="vinculacion-skeleton" className="animate-pulse space-y-3">
          <div className="mx-auto h-5 w-2/3 rounded bg-slate-200" />
          <div className="mx-auto mb-4 h-4 w-1/2 rounded bg-slate-200" />
          <div className="h-16 rounded-xl bg-slate-100" />
          <div className="h-20 rounded-xl bg-slate-100" />
          <div className="h-24 rounded-xl bg-slate-100" />
          <div className="h-10 rounded-md bg-slate-200" />
        </div>
      </Shell>
    );
  }

  if (isError) {
    const status = error instanceof ApiError ? error.status : null;

    if (status === 404) {
      return (
        <Shell>
          <MensajeConContacto
            titulo="Este enlace de vinculación no es válido"
            cuerpo="Revisa que copiaste la dirección completa del correo. Si el problema sigue, escríbenos y te ayudamos."
          />
        </Shell>
      );
    }

    if (status === 410) {
      return (
        <Shell>
          <MensajeConContacto
            titulo="Este enlace venció o fue reemplazado — pide a Kaptar uno nuevo"
            cuerpo="Es normal si pasó tiempo desde que llegó el correo, o si te reenviaron uno más reciente. Escríbenos y te generamos uno nuevo."
          />
        </Shell>
      );
    }

    return (
      <Shell>
        <MensajeConContacto
          titulo="No pudimos cargar tu enlace de vinculación"
          cuerpo={error instanceof Error ? error.message : ERROR_GENERICO_CARGA}
        />
      </Shell>
    );
  }

  if (!data) return null;

  const activa = Boolean(pago) || data.paid || data.estado_funnel === "activa";
  const tier = pago?.tier ?? data.tier;
  const period = pago?.tier_period ?? data.period;
  const total = precioDe(tier, period);
  const municipio = data.municipality_name || data.tenant_display_name || "tu municipio";

  return (
    <Shell>
      <h2 className="mb-1 text-center text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
        Vinculación de la {data.tenant_display_name || data.municipality_name}
      </h2>
      <p className="mx-auto mb-6 max-w-md text-center text-sm text-slate-600">
        Membresía {tierLabel(tier)} · {periodLabel(period)} — así va el proceso:
      </p>

      <Stepper activa={activa} />

      <div className="mb-3 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-400 uppercase">
          Membresía a pagar
        </h3>
        <div className="flex justify-between gap-4 py-1 text-sm">
          <span className="text-slate-600">Plan</span>
          <span className="text-right font-semibold text-slate-900">
            {tierLabel(tier)} · {periodLabel(period)}
          </span>
        </div>
        <div className="flex justify-between gap-4 py-1 text-sm">
          <span className="text-slate-600">Total</span>
          <span data-testid="vinculacion-total" className="text-right font-semibold text-slate-900">
            {total !== null ? formatoCOP(total) : "Consulta con soporte"}
          </span>
        </div>
      </div>

      {!activa && (
        <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-400 uppercase">
            Qué pasa al pagar
          </h3>
          <ul className="flex flex-col gap-2.5">
            <li className="flex items-start gap-2 text-sm text-slate-600">
              <CheckIcon />
              <span>
                Las invitaciones salen solas a los{" "}
                <strong>{data.invitations_count} correos del contrato</strong>
              </span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-600">
              <CheckIcon />
              <span>Cada persona crea su contraseña y completa su perfil — 2 pasos</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-600">
              <CheckIcon />
              <span>
                El Radar de {municipio} queda <strong>activo desde el día 1</strong>
              </span>
            </li>
          </ul>
        </div>
      )}

      {activa && (
        <div className="mb-5 rounded-xl border border-cta-100 bg-cta-50 p-4">
          <h3 className="mb-1 text-sm font-bold text-cta-700">
            {pago ? "¡Listo! La membresía quedó activa" : "La membresía ya está activa"}
          </h3>
          {pago && pago.invitaciones.length > 0 ? (
            <>
              <p className="mb-3 text-sm text-slate-600">
                Ya enviamos las invitaciones — cada persona recibe un correo para crear su
                contraseña.
              </p>
              <ul data-testid="vinculacion-invitaciones" className="flex flex-col gap-2">
                {pago.invitaciones.map((inv, idx) => (
                  <li
                    key={`${inv.email_masked}-${idx}`}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="text-slate-800">{inv.email_masked}</span>
                    <span className="text-xs font-semibold text-cta-700">
                      {INVITACION_STATUS_LABELS[inv.status]}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            // Sin lista que mostrar: el GET no la trae (link reabierto ya
            // pagado) o el POST volvió con `invitaciones: []` (pago
            // idempotente -- las invitaciones ya habían salido antes, ver
            // punto 4 del docstring). En ambos casos la cifra real es
            // `invitations_count` del GET, no una lista vacía con un
            // "Ya enviamos —" colgando.
            <p className="text-sm text-slate-600">
              Ya se enviaron las invitaciones a los {data.invitations_count} correos del contrato.
            </p>
          )}
        </div>
      )}

      {mutation.isError && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          {mutation.error instanceof Error ? mutation.error.message : ERROR_GENERICO_PAGO}
        </div>
      )}

      {!activa && (
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="block w-full rounded-md bg-cta-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-cta-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? "Procesando el pago…" : "Pagar la membresía"}
        </button>
      )}

      {activa && (
        <Link
          to="/login"
          className="block w-full rounded-md bg-cta-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-cta-700"
        >
          Ir a Kaptar
        </Link>
      )}

      <p className="mt-3 text-center text-xs text-slate-500">
        {activa ? (
          <>
            ¿Preguntas?{" "}
            <a href={SOPORTE_MAILTO} className="font-semibold text-brand-700 underline">
              soporte@kaptar.com.co
            </a>{" "}
            · respondemos el mismo día hábil
          </>
        ) : (
          <>
            ¿Preguntas antes de pagar?{" "}
            <a href={SOPORTE_MAILTO} className="font-semibold text-brand-700 underline">
              soporte@kaptar.com.co
            </a>{" "}
            · respondemos el mismo día hábil
          </>
        )}
      </p>
    </Shell>
  );
}

// --- Piezas compartidas -----------------------------------------------

/** Layout comun a todos los estados: brand centrado + card blanca. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-10">
      <div className="w-full max-w-xl">
        <header className="mb-6 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600">
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
              <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
              <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="text-lg font-extrabold tracking-tight text-slate-900">Kaptar</h1>
        </header>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs sm:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}

function MensajeConContacto({ titulo, cuerpo }: { titulo: string; cuerpo: string }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-center">
      <h2 className="mb-2 text-lg font-bold text-amber-900">{titulo}</h2>
      <p className="mb-4 text-sm leading-relaxed text-amber-800">{cuerpo}</p>
      <a
        href={SOPORTE_MAILTO}
        className="inline-block rounded-md bg-cta-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-cta-700"
      >
        Contactar soporte
      </a>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 shrink-0 text-cta-600"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// --- Stepper -------------------------------------------------------------

type StepState = "done" | "now" | "todo";

interface StepDef {
  label: string;
  state: StepState;
  sub?: string;
}

function stepDotClasses(state: StepState): string {
  if (state === "done") return "border-cta-500 bg-cta-50 text-cta-700";
  if (state === "now") return "border-brand-600 bg-brand-600 text-white ring-4 ring-brand-100";
  return "border-slate-200 bg-white text-slate-400";
}

/**
 * 4 pasos fijos: 1-2 siempre "done" (si hay token de vinculación, ya se
 * negoció la propuesta y se firmó el contrato). 3-4 dependen de `activa`
 * (paid). El paso 4 muestra el copy de activación cuando ya se pagó.
 */
function Stepper({ activa }: { activa: boolean }) {
  const steps: StepDef[] = [
    { label: "Propuesta aceptada", state: "done" },
    { label: "Contrato firmado", state: "done" },
    { label: "Pago", state: activa ? "done" : "now" },
    {
      label: "Activación",
      state: activa ? "done" : "todo",
      sub: activa ? "Activa — invitaciones enviadas" : undefined,
    },
  ];

  return (
    // `isolate`: el conector de cada paso va con `-z-10` para quedar
    // debajo del círculo, pero `li.relative` no crea stacking context,
    // así que sin uno propio el z negativo lo mandaba debajo del
    // `bg-white` de la card y las líneas no se veían (fix final F4).
    // Con `isolation: isolate` en el <ol>, el conector queda debajo del
    // círculo y encima del fondo de la card.
    <ol aria-label="Progreso de la vinculación" className="isolate mb-6 flex items-start">
      {steps.map((step, idx) => (
        <li key={step.label} className="relative flex-1 px-1 text-center">
          {idx < steps.length - 1 && (
            <div
              aria-hidden="true"
              className={
                "absolute top-3 left-1/2 -z-10 h-0.5 w-full " +
                (step.state === "done" ? "bg-cta-500" : "bg-slate-200")
              }
            />
          )}
          <div
            className={
              "relative z-10 mx-auto mb-1.5 flex h-6 w-6 items-center justify-center rounded-full border-2 text-[11px] font-bold " +
              stepDotClasses(step.state)
            }
          >
            {step.state === "done" ? "✓" : idx + 1}
          </div>
          <div
            className={
              "text-xs font-semibold " +
              (step.state === "todo" ? "text-slate-400" : "text-slate-900")
            }
          >
            {step.label}
          </div>
          <div className="min-h-[1.2em] text-[11px] text-slate-400">
            {step.sub ?? (step.state === "now" ? "en curso" : "")}
          </div>
        </li>
      ))}
    </ol>
  );
}
